import { getPointsFromCoord } from './rect.js';
import { DEFAULT_RECT_STYLE, RECT_TITLE_STYLES, DEFAULT_MESSAGES } from '../const/constants.js';





/**
 * 处理教育类响应的 QuestionInfo，生成右侧结果树可点击的矩形列表
 *
 * 多页/跨页规则：
 *  - QuestionInfo[i] 每项对应一张输入图（页），i+1 即页码
 *  - ResultList[n].Coord 为数组，跨栏或跨页时可含多个框；这里每个框生成一个 rect
 *  - 子题（Question[j].ResultList）内子片段可用 PageIndex 精确定位所在页
 *  - key 前缀 `p${pageNum}_` 确保跨页时唯一，并与 EduRenderer / getRectPosition 保持一致
 *  - 单页调用（QuestionInfo 长度 === 1）行为对齐单页语义，PageIndex 缺省时回退到 1
 *
 * @param {Object} data - 后端响应，需包含 QuestionInfo 数组
 * @param {boolean} dealItem - 是否递归处理子题
 * @param {number} fallbackPageNum - 兼容旧调用（同步接口传入 PdfPageNumber）
 * @returns {Array} rect 数组
 */
export const dealEduAgentRect = (data, dealItem = true, fallbackPageNum = 1) => {
  const questionInfoList = Array.isArray(data?.QuestionInfo) ? data.QuestionInfo : [];
  if (questionInfoList.length === 0) return [];

  const isMultiPage = questionInfoList.length > 1;
  const allRects = [];

  questionInfoList.forEach((pageInfo, pageIdx) => {
    if (!pageInfo?.ResultList?.length) return;
    // 单页调用时用旧的 fallbackPageNum；多页时用 QuestionInfo 索引
    const _basePage = isMultiPage ? pageIdx + 1 : fallbackPageNum;
    const _height = pageInfo.Height;
    const _width = pageInfo.Width;

    // key 生成：单页时保持旧格式（Question01），多页时加页码前缀避免冲突
    const buildQuestionKey = (questionId, coordIdx) =>
      isMultiPage
        ? `Question_p${_basePage}_${questionId}1_c${coordIdx}`
        : `Question${questionId}1`;
    const buildChildKey = (questionId, childId, pageNum, coordIdx) =>
      isMultiPage
        ? `Question_p${pageNum}_${questionId}${childId}2_c${coordIdx}`
        : `Question${questionId}${childId}2`;

    pageInfo.ResultList.forEach((question, questionId) => {
      // 整题 Coord 数组化（跨栏/跨页时含多框）
      const coordArr = Array.isArray(question.Coord)
        ? question.Coord
        : (question.Coord ? [question.Coord] : []);

      coordArr.forEach((coord, coordIdx) => {
        // 单页且原来只取 Coord[0]，为兼容旧行为，非首框在单页模式下跳过
        if (!isMultiPage && coordIdx > 0) return;
        const key = buildQuestionKey(questionId, coordIdx);
        allRects.push({
          ...DEFAULT_RECT_STYLE,
          titleStyle: RECT_TITLE_STYLES.primary,
          label: `题目${questionId + 1}`,
          value: key,
          key,
          OriginImgHeight: _height,
          OriginImgWidth: _width,
          pageNum: _basePage,
          points: getPointsFromCoord(coord)
        });
      });

      if (!dealItem) return;

      // 子题目：可能跨页，用 PageIndex 精确定位
      const questionList = (question.Question || [])
        .map(i => i.ResultList)
        .flat(Infinity)
        .filter(item => item != null);

      questionList.forEach((childItem, childId) => {
        const childCoordArr = Array.isArray(childItem.Coord)
          ? childItem.Coord
          : (childItem.Coord ? [childItem.Coord] : []);
        // 子题的 pageNum 优先读它自身的 PageIndex（跨页时准确落到具体页）
        const _childPage = childItem.PageIndex || _basePage;
        const childPageInfo = questionInfoList[_childPage - 1] || pageInfo;

        childCoordArr.forEach((coord, coordIdx) => {
          if (!isMultiPage && coordIdx > 0) return;
          const key = buildChildKey(questionId, childId, _childPage, coordIdx);
          allRects.push({
            ...DEFAULT_RECT_STYLE,
            titleStyle: RECT_TITLE_STYLES.success,
            label: `子题目${questionId + 1} - ${childId + 1}`,
            type: 'children',
            value: key,
            key,
            OriginImgHeight: childPageInfo.Height,
            OriginImgWidth: childPageInfo.Width,
            pageNum: _childPage,
            points: getPointsFromCoord(coord)
          });
        });
      });
    });
  });

  return allRects;
}

/**
 * 处理 MarkInfos 数据的坐标，生成答案矩形框
 * @param {Object} data - 包含 MarkInfos 的响应数据
 * @param {Object} questionData - QuestionInfo 数据，用于获取图片尺寸
 * @param {number} pageNum - 页面编号（用于多页 PDF 场景）
 * @returns {Array} 矩形框数组
 */
export const dealMarkInfoRect = (data, questionData, pageNum = 1) => {
  const groupOptions = [];
  const markInfos = data?.data?.Response?.MarkInfos || data?.Response?.MarkInfos || data?.MarkInfos || [];
  const questionInfo = questionData?.QuestionInfo?.[0] || data?.data?.Response?.QuestionInfo?.[0] || data?.QuestionInfo?.[0] || {};
  const imgHeight = questionInfo.Height || 0;
  const imgWidth = questionInfo.Width || 0;

  const createAnswerRect = (answer, key, value, type, titleStyle, extraProps = {}) => {
    const positions = answer.HandwriteInfoPostions || answer.HandwriteInfoPositions || [];
    if (positions.length !== 8) return null;
    return {
      ...DEFAULT_RECT_STYLE,
      stroke: 'transparent',
      IsCorrect: answer.IsCorrect,
      titleStyle,
      label: '',
      type,
      value,
      key,
      OriginImgHeight: imgHeight,
      OriginImgWidth: imgWidth,
      pageNum,
      points: positions,
      ...extraProps
    };
  };

  // 根据 QuestionPositions 创建题干定位矩形框
  const createPositionRect = (positions, key, type, titleStyle, extraProps = {}) => {
    if (!positions || positions.length !== 8) return null;
    return {
      ...DEFAULT_RECT_STYLE,
      stroke: 'transparent',
      titleStyle,
      label: '',
      type,
      value: key,
      key,
      OriginImgHeight: imgHeight,
      OriginImgWidth: imgWidth,
      pageNum,
      points: positions,
      ...extraProps
    };
  };

  markInfos.forEach((markItem, questionId) => {
    const answerInfos = markItem.AnswerInfos || [];
    const answerRects = [];

    answerInfos.forEach((answer, answerIdx) => {
      const rect = createAnswerRect(
        answer,
        `markInfo${questionId}_${answerIdx}`,
        `markInfo${questionId}_${answerIdx}`,
        'answer',
        RECT_TITLE_STYLES.warning,
        { questionId, answerIdx }
      );
      if (rect) answerRects.push(rect);
    });

    // 处理大题级别的 QuestionImagePositions（题目配图位置）
    const questionImagePositions = markItem.QuestionImagePositions || [];
    questionImagePositions.forEach((imgPos, imgIdx) => {
      const posArray = imgPos?.Position || [];
      if (posArray.length === 8) {
        const imgPosRect = createPositionRect(
          posArray,
          `markInfo${questionId}_imgpos${imgIdx}`,
          'questionImagePosition',
          RECT_TITLE_STYLES.primary,
          { questionId, imgIdx }
        );
        if (imgPosRect) answerRects.push(imgPosRect);
      }
    });

    // 递归处理嵌套的 MarkInfos（子题目）
    const subMarkInfos = markItem.MarkInfos || [];
    subMarkInfos.forEach((subMarkItem, subIdx) => {
      // 通过 QuestionPositions  定位子题 MarkItemTitle
      const subQPos = subMarkItem.QuestionPositions || [];
      if (subQPos.length > 0) {
        const subQPosRect = createPositionRect(
          subQPos,
          `markInfo${questionId}_sub${subIdx}_qpos`,
          'subQuestionPosition',
          RECT_TITLE_STYLES.primary,
          { questionId, subIdx }
        );
        if (subQPosRect) answerRects.push(subQPosRect);
      }

      // 处理子题级别的 QuestionImagePositions（子题配图位置）
      const subImagePositions = subMarkItem.QuestionImagePositions || [];
      subImagePositions.forEach((imgPos, imgIdx) => {
        const posArray = imgPos?.Position || [];
        if (posArray.length === 8) {
          const subImgPosRect = createPositionRect(
            posArray,
            `markInfo${questionId}_sub${subIdx}_imgpos${imgIdx}`,
            'subQuestionImagePosition',
            RECT_TITLE_STYLES.primary,
            { questionId, subIdx, imgIdx }
          );
          if (subImgPosRect) answerRects.push(subImgPosRect);
        }
      });

      const subAnswerInfos = subMarkItem.AnswerInfos || [];
      subAnswerInfos.forEach((answer, answerIdx) => {
        const rect = createAnswerRect(
          answer,
          `markInfo${questionId}_sub${subIdx}_${answerIdx}`,
          `markInfo${questionId}_sub${subIdx}_${answerIdx}`,
          'subAnswer',
          RECT_TITLE_STYLES.success,
          { questionId, subIdx, answerIdx }
        );
        if (rect) answerRects.push(rect);
      });
    });

    if (answerRects.length > 0) {
      groupOptions.push({
        group: `题目${markItem.MarkItemTitle || questionId + 1}`,
        children: answerRects
      });
    }
  });

  return groupOptions.map(i => i.children).flat();
}

/**
 * 处理 Essay SentenceSuggests 数据的坐标，生成矩形框
 * @param {Object} data - 包含 SentenceSuggests 的响应数据
 * @param {number} imgHeight - 图片高度
 * @param {number} imgWidth - 图片宽度
 * @param {number} pageNum - 页面编号（用于多页 PDF 场景）
 * @returns {Array} 矩形框数组
 */
export const dealEssayRect = (data, imgHeight = 0, imgWidth = 0, pageNum = 1) => {
  const rectList = [];
  const sentenceSuggests = data?.data?.Response?.SentenceSuggests || data?.Response?.SentenceSuggests || data?.SentenceSuggests || [];

  sentenceSuggests.forEach((item, index) => {
    const positions = item.Positions || [];
    const itemKey = `essay_${index}`;
    
    positions.forEach((posItem, posIndex) => {
      const posArray = posItem?.Position;
      if (posArray?.length === 8) {
        rectList.push({
          ...DEFAULT_RECT_STYLE,
          titleStyle: RECT_TITLE_STYLES.warning,
          label: positions.length > 1 ? `${item.SubType || item.Type}-${posIndex + 1}` : (item.SubType || item.Type),
          type: item.Type,
          subType: item.SubType,
          value: itemKey,
          key: itemKey,
          posIndex,
          OriginImgHeight: imgHeight,
          OriginImgWidth: imgWidth,
          pageNum,
          points: posArray
        });
      }
    });
  });

  return rectList;
}


//  TODO： 后期改为konva内置的方法
export const cropToBase64 = (image, points,curImgSize) => {
  return new Promise(async (resolve, reject) => {
  if (!image) resolve("");

  const img = new Image();
  let radio = 1 ;
  let _points = points ;
  img.src = image;
  img.crossOrigin = 'Anonymous';
  img.onload = () => {
      if(curImgSize){
        radio = img.width / curImgSize.width
        _points = _points.map(i => (
          {
          x: i.x * radio,
          y: i.y * radio,
        }
        ))
      }

    // 定义四边形的四个点（示例坐标）
    // const points = [
    //   { x: 100, y: 100 }, // 左上
    //   { x: 300, y: 150 }, // 右上
    //   { x: 280, y: 350 }, // 右下
    //   { x: 80, y: 300 }   // 左下
    // ];

    // 创建离屏 Canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // 设置 Canvas 尺寸为四边形外接矩形
    const minX = Math.min(..._points.map(p => p.x));
    const maxX = Math.max(..._points.map(p => p.x));
    const minY = Math.min(..._points.map(p => p.y));
    const maxY = Math.max(..._points.map(p => p.y));
    canvas.width = maxX - minX;
    canvas.height = maxY - minY;

    // 创建裁剪路径（四边形）
    ctx.beginPath();
    ctx.moveTo(_points[0].x - minX, _points[0].y - minY);
    _points.forEach(point => {
      ctx.lineTo(point.x - minX, point.y - minY);
    });
    ctx.closePath();
    ctx.clip();
    // 绘制图像（偏移到裁剪区域）
    ctx.drawImage(
      img,
      minX, minY, // 源图像起点
      canvas.width, canvas.height, // 源图像宽高
      0, 0, // 目标起点
      canvas.width, canvas.height // 目标宽高
    );
    // 获取 Base64
    const base64 = canvas.toDataURL('image/png');
    resolve(base64);
  }
  img.onerror = function (err) {
      console.log(DEFAULT_MESSAGES.TASK_IMAGE_RECOGNITION_FAILED, err)
      resolve("");
    };
  })
};
